import type { LoaderFunctionArgs } from 'react-router-dom'
import { useLoaderData } from 'react-router-dom'
import { redirect } from 'react-router-dom'
import Debug from 'debug'

import { isAuthenticated } from '@jotta/auth-client/useAuthStatus'
import { lookupLink } from '@jotta/grpc-js-client/configService'
import { fromEncodedContentRef } from '@jotta/grpc-js-client/ref'
import { ExternalLinkRef } from '@jotta/grpc-web/no/jotta/openapi/ref_pb'
import { createLoaderRoute } from '@jotta/router'
import { ErrorElement } from '@jotta/ui/ErrorElement'
import { Redirect } from '@jotta/ui/Redirect'

import { contentRefToRoute } from './contentRefToRoute'

const debug = Debug('jotta:webapp:ContentRefRoute')

function ContentRefRoute() {
  const loaderData = useLoaderData()
  const route = typeof loaderData === 'string' ? loaderData : '/'
  return <Redirect replace to={route} />
}

export const contentRefRoute = ['r', 'w'].map(
  prefix =>
    createLoaderRoute({
      id: `contentRef/${prefix}`,
      path: `${prefix}/:encodedContentRef?`,
      element: <ContentRefRoute />,
      errorElement: (
        <ErrorElement
          captureContext={scope => scope.setTag('key', 'contentRref')}
        />
      ),
      loader: async ({ params, request }: LoaderFunctionArgs) => {
        const { encodedContentRef = '' } = params
        const url = new URL(request.url)
        const username = url.searchParams.get('username') || ''
        const token = url.searchParams.get('token') || ''
        const action = url.searchParams.get('action') || ''
        const authenticated = await isAuthenticated()
        const result = fromEncodedContentRef(encodedContentRef)
        if (result.isErr()) {
          debug('Error decoding ref', result.error)
          return redirect('/web/sync')
        }
        const ref = result.value
        const route = contentRefToRoute({
          contentRef: ref,
          authenticated,
        })
        debug('contentRefToRoute', ref, route)
        if (route instanceof ExternalLinkRef) {
          const res = await lookupLink(route)
          window.location.replace(res.getValidLink())
          return null
        }
        const redirectUrl = new URL(route, url.origin)
        if (/^(share|edit)$/.test(action)) {
          redirectUrl.searchParams.set('action', action)
        }
        const redirectTo = redirectUrl.pathname + redirectUrl.search
        if (username && token && !authenticated) {
          const autoLoginUrl = new URL('/api/autologin', url.origin)
          autoLoginUrl.searchParams.set('username', username)
          autoLoginUrl.searchParams.set('token', token)
          autoLoginUrl.searchParams.set('redirect_uri', redirectTo)
          window.location.replace(autoLoginUrl)
          return null
        }
        return redirectTo
      },
    }).route,
)
